import { EmailTemplateTypes, IInvalidField } from "shared";
import { useEffect, useMemo } from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { When } from "react-if";

import { useEmailTemplates } from "../../../../../hooks/useEmailTemplates";
import { Select } from "../../../../../../common/Atoms/Select";
import { Paragraph } from "../../../../../../common/Atoms/Typography/Paragraph";
import { WrappedLink } from "../../../../WrappedLink";

/** Dropdown value for the Intelligent Dynamic option. Stored as policy.ai_prompt, never as template_id */
export const INTELLIGENT_DYNAMIC_TEMPLATE_ID = `intelligent-dynamic`;

interface EmailTemplateSelectorProps {
  type: EmailTemplateTypes;
  handleChange: ({ value, name }: { value: string; name: string }) => void;
  value: string | null;
  invalid?: boolean | IInvalidField;
  name: string;
  nullable?: boolean;
  label?: string;
  description?: string;
  emptyText?: string;
  helpIcon?: string;
  currentPolicyId: number;
  /** Adds the Intelligent Dynamic option, where AI writes each email */
  intelligentDynamic?: boolean;
}

export function EmailTemplateSelector({
  type,
  handleChange,
  value,
  invalid,
  name,
  nullable,
  label,
  description,
  emptyText,
  helpIcon,
  currentPolicyId,
  intelligentDynamic,
}: EmailTemplateSelectorProps) {
  const { data: emailTemplates, isSuccess, isLoading } = useEmailTemplates();

  const filteredEmailTemplates = useMemo(() => {
    if (isSuccess) {
      // If late fee, include legacy
      const filterFn = type === EmailTemplateTypes.LATE_FEE ? t => (
        (t.type === type || t.type === EmailTemplateTypes.LEGACY_LATE_FEE) &&
        t.version > 0 // Ensures the user has saved it at least once
      ) : t => (
        (t.type === type) &&
        t.version > 0 // Ensures the user has saved it at least once
      );

      const templates = emailTemplates
        .filter(filterFn)
        .map(t => {
          const otherPolicies = t.policies.filter(p => p.id !== currentPolicyId);
          const hasOtherPolicies = otherPolicies.length > 0;
          const label = (
            <span className={ `flex` }>
              { t.name }
              <WrappedLink
                to={ `/settings/email-templates/${t.id}` }
                target={ `_blank` }
              >
                <Paragraph
                  as={ `span` }
                  className={ `flex ml-1` }
                  variant={ `link` }>
                  { `Edit` }
                  <ArrowTopRightOnSquareIcon className={ `w-4 h-4 ml-1` } />
                </Paragraph>
              </WrappedLink>

              <When condition={ t.children.length > 0 }>
                <Paragraph
                  as={ `span` }
                  className={ `ml-1 text-xs text-gray-500 self-center` }
                >
                  { `(${t.children.length} ${t.children.length === 1 ? `variation` : `variations`})` }
                </Paragraph>
              </When>

              <When condition={ hasOtherPolicies }>
                <Paragraph
                  as={ `span` }
                  className={ `ml-1 text-xs text-gray-500 self-center` }
                >
                  { `(used in ${otherPolicies.length} other ${otherPolicies.length === 1 ? `policy`: `policies`})` }
                </Paragraph>
              </When>
            </span>
          );

          return {
            label,
            value: t.id,
          };
        });

      if (intelligentDynamic) {
        templates.unshift({
          label: <span>{ `Intelligent Dynamic (AI writes each email)` }</span>,
          value: INTELLIGENT_DYNAMIC_TEMPLATE_ID,
        });
      }

      return templates;
    }

    return [];
  }, [emailTemplates, isSuccess, type, intelligentDynamic]);

  const emptyTextChecked = useMemo(() => {
    return (emptyText && filteredEmailTemplates.length === 0) ? `No templates for this type exist` : emptyText;
  }, [emailTemplates, isSuccess, filteredEmailTemplates]);

  // If the template no longer is valid for the type, then remove from formdata too
  useEffect(() => {
    const currentTemplateInFiltered = filteredEmailTemplates.find(t => t.value === value);

    if (!currentTemplateInFiltered && value && emailTemplates?.length > 0) {
      // Templates loaded, value is defined, but not in filteredEmailTemplates
      // Remove from formdata
      console.log(`Removing invalid template`);
      handleChange({ value: null, name });
    }
  }, [value, emailTemplates, filteredEmailTemplates, handleChange, name]);

  return (
    <Select
      options={ filteredEmailTemplates }
      selected={ filteredEmailTemplates.find(t => t.value === value) ? value : null } // don't pass a value if it doesn't exist in filteredEmailTemplates
      name={ name }
      onChange={ e => handleChange(e) }
      invalid={ invalid }
      label={ label }
      description={ description }
      nullable={ !!nullable }
      emptyText={ emptyTextChecked }
      helpIcon={ helpIcon }
      loading={ isLoading }
    />
  );
}
